"use client";

import React, { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  createWarRoomTaskAction,
  createWarRoomRiskAction,
  createWarRoomDecisionAction,
} from "@/actions/war-room-actions";
import {
  Mic,
  MicOff,
  PhoneOff,
  Play,
  Sparkles,
  CheckCircle,
  Database,
  AlertTriangle,
  FileCheck,
  TrendingUp,
  Clock,
  User,
  Plus,
} from "lucide-react";

interface ProjectItem {
  id: string;
  title: string;
}

interface AgentItem {
  id: string;
  name: string;
  type: string;
  role: string;
}

interface WarRoomClientProps {
  initialProjects: ProjectItem[];
  initialAgents: AgentItem[];
}

interface TranscriptLine {
  id: string;
  speaker: string;
  text: string;
  time: string;
  isAi?: boolean;
}

interface DetectedInsight {
  id: string;
  type: "task" | "risk" | "decision";
  title: string;
  synced: boolean;
  syncId?: string;
  loading: boolean;
  // Field values that can be modified before sync
  projectId?: string;
  assigneeId?: string;
  priority?: "low" | "medium" | "high" | "critical";
  severity?: "low" | "medium" | "high" | "critical";
  context?: string;
}

const TRANSCRIPT_SEQUENCE: {
  delay: number;
  speaker: string;
  text: string;
  isAi?: boolean;
  triggersInsightId?: string;
}[] = [
  {
    delay: 2000,
    speaker: "Alessandro (CEO)",
    text: "Buongiorno a tutti. Dobbiamo fare il punto sulla roadmap di sviluppo. C'è un problema grosso: l'integrazione con le API sandbox di Stripe sta bloccando i milestone frontend.",
  },
  {
    delay: 6000,
    speaker: "AI Strategist",
    isAi: true,
    text: "Ho rilevato un potenziale rischio per la timeline del progetto: 'Stripe Sandbox API Blocking Frontend Milestones'. Desideri registrarlo come Rischio strategico?",
    triggersInsightId: "i-risk-stripe",
  },
  {
    delay: 11000,
    speaker: "Giulia (Head of Ops)",
    text: "Assolutamente sì. E dobbiamo anche rimediare al bug di sincronizzazione core di Quario. Direi di assegnare subito a Marco il task di risolvere questo bug.",
  },
  {
    delay: 15000,
    speaker: "AI Strategist",
    isAi: true,
    text: "Rilevato task operativo: 'Risolvere bug sincronizzazione core Quario'. Suggerisco priorità Alta, assegnatario: Marco Rossi. Vuoi inserirlo nel backlog?",
    triggersInsightId: "i-task-bug",
  },
  {
    delay: 20000,
    speaker: "Alessandro (CEO)",
    text: "Perfetto. Inoltre, data l'incertezza, decidiamo formalmente di posticipare il rilascio della Beta dal 1 luglio al 15 agosto per assicurarci la stabilità dell'infrastruttura.",
  },
  {
    delay: 24000,
    speaker: "AI Strategist",
    isAi: true,
    text: "Rilevata decisione strategica: 'Posticipo rilascio Beta al 15 Agosto'. Contesto: Stabilità infrastruttura di pagamento. Desideri registrarla?",
    triggersInsightId: "i-dec-beta",
  },
];

const INSIGHTS_TEMPLATES: Record<string, DetectedInsight> = {
  "i-risk-stripe": {
    id: "i-risk-stripe",
    type: "risk",
    title: "Blocco milestone frontend a causa di anomalie API sandbox Stripe",
    severity: "high",
    synced: false,
    loading: false,
  },
  "i-task-bug": {
    id: "i-task-bug",
    type: "task",
    title: "Risolvere bug sincronizzazione core Quario Cloud",
    priority: "high",
    synced: false,
    loading: false,
  },
  "i-dec-beta": {
    id: "i-dec-beta",
    type: "decision",
    title: "Posticipo rilascio versione Beta al 15 Agosto 2026",
    context:
      "Spostamento della data di rilascio a causa dell'instabilità delle API sandbox Stripe per garantire una build stabile.",
    synced: false,
    loading: false,
  },
};

export function WarRoomClient({
  initialProjects,
  initialAgents,
}: WarRoomClientProps) {
  const [meetingActive, setMeetingActive] = useState(false);
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
  const [detectedInsights, setDetectedInsights] = useState<DetectedInsight[]>(
    [],
  );
  const [errorMessage, setErrorMessage] = useState("");

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const sequenceTimers = useRef<NodeJS.Timeout[]>([]);
  const animationFrameId = useRef<number | null>(null);

  // Auto-scroll transcript when new items arrive
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcript]);

  // Audio wave visualizer animation
  useEffect(() => {
    if (!meetingActive) {
      if (animationFrameId.current)
        cancelAnimationFrame(animationFrameId.current);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = canvas.parentElement?.clientWidth || 400;
    canvas.height = 80;

    let bars = Array.from({ length: 45 }, () => 2);
    let phase = 0;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "rgba(100, 116, 139, 0.2)"; // Muted fill

      const barWidth = canvas.width / bars.length;
      const spacing = 2;

      phase += 0.08;

      for (let i = 0; i < bars.length; i++) {
        // Generate values resembling speaking levels
        let amplitude = muted
          ? 0.05
          : 0.2 +
            0.8 * Math.sin(phase + i * 0.15) * Math.cos(phase * 0.4 + i * 0.05);
        if (amplitude < 0) amplitude = 0.05;

        // Fluctuations
        bars[i] = bars[i] * 0.7 + amplitude * canvas.height * 0.8 * 0.3;

        const x = i * barWidth;
        const h = bars[i];
        const y = (canvas.height - h) / 2;

        // Color based on activity
        if (muted) {
          ctx.fillStyle = "var(--color-text-muted, #7c8594)";
        } else {
          ctx.fillStyle = "var(--color-primary-500, #3b82f6)";
        }

        ctx.beginPath();
        ctx.roundRect(x + spacing, y, barWidth - spacing * 2, h, 3);
        ctx.fill();
      }

      animationFrameId.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationFrameId.current)
        cancelAnimationFrame(animationFrameId.current);
    };
  }, [meetingActive, muted]);

  // Timer counter
  useEffect(() => {
    if (meetingActive) {
      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      setDuration(0);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [meetingActive]);

  // Start Meeting Session
  const startMeeting = () => {
    setErrorMessage("");
    setMeetingActive(true);
    setTranscript([
      {
        id: "start",
        speaker: "Sistema",
        text: "Sessione War Room audio avviata. Copilota AI in ascolto attivo...",
        time: "00:00",
        isAi: true,
      },
    ]);
    setDetectedInsights([]);

    // Queue mock speech sequences
    TRANSCRIPT_SEQUENCE.forEach((item) => {
      const timer = setTimeout(() => {
        // Add speech line
        const min = Math.floor(item.delay / 1000 / 60);
        const sec = Math.floor((item.delay / 1000) % 60);
        const formattedTime = `${min.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;

        setTranscript((prev) => [
          ...prev,
          {
            id: `line-${item.delay}`,
            speaker: item.speaker,
            text: item.text,
            time: formattedTime,
            isAi: item.isAi,
          },
        ]);

        // If it triggers a strategic insight card
        if (item.triggersInsightId) {
          const tmpl = INSIGHTS_TEMPLATES[item.triggersInsightId];
          // Pre-populate dropdown targets if we can
          const updatedTmpl = {
            ...tmpl,
            projectId: initialProjects[0]?.id || "",
            assigneeId: initialAgents[0]?.id || "",
          };
          setDetectedInsights((prev) => [...prev, updatedTmpl]);
        }
      }, item.delay);

      sequenceTimers.current.push(timer);
    });
  };

  // End Meeting Session
  const endMeeting = () => {
    setMeetingActive(false);
    sequenceTimers.current.forEach((t) => clearTimeout(t));
    sequenceTimers.current = [];
    setTranscript((prev) => [
      ...prev,
      {
        id: "end",
        speaker: "Sistema",
        text: "Sessione terminata. Tutte le trascrizioni sono state salvate.",
        time: formatTime(duration),
        isAi: true,
      },
    ]);
  };

  // Sincronizzazione di un'insight nel Database
  const handleSyncInsight = async (insightId: string) => {
    const target = detectedInsights.find((d) => d.id === insightId);
    if (!target) return;

    // Set loading
    setDetectedInsights((prev) =>
      prev.map((d) => (d.id === insightId ? { ...d, loading: true } : d)),
    );
    setErrorMessage("");

    try {
      let res;
      if (target.type === "task") {
        if (!target.projectId) {
          throw new Error("Seleziona un progetto per salvare il task.");
        }
        res = await createWarRoomTaskAction({
          projectId: target.projectId,
          title: target.title,
          priority: target.priority || "medium",
          assigneeId: target.assigneeId || null,
        });
      } else if (target.type === "risk") {
        res = await createWarRoomRiskAction({
          title: target.title,
          severity: target.severity || "medium",
        });
      } else {
        // decision
        res = await createWarRoomDecisionAction({
          title: target.title,
          context: target.context || "",
          deciderId: target.assigneeId || null,
        });
      }

      if (res?.error) {
        throw new Error(res.error);
      }

      // Mark as synced
      setDetectedInsights((prev) =>
        prev.map((d) =>
          d.id === insightId
            ? { ...d, synced: true, syncId: res?.id, loading: false }
            : d,
        ),
      );
    } catch (err: any) {
      setErrorMessage(err.message || "Errore di sincronizzazione");
      setDetectedInsights((prev) =>
        prev.map((d) => (d.id === insightId ? { ...d, loading: false } : d)),
      );
    }
  };

  // Formatter helpers
  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  // Modify form state directly in array for active inputs
  const updateInsightField = (
    id: string,
    field: keyof DetectedInsight,
    value: any,
  ) => {
    setDetectedInsights((prev) =>
      prev.map((d) => (d.id === id ? { ...d, [field]: value } : d)),
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      {/* LEFT COLUMN: Meeting Console & Live Transcript */}
      <div className="lg:col-span-7 space-y-6 flex flex-col h-[680px]">
        {/* Call Management Panel */}
        <Card className="border border-border flex flex-col justify-between shrink-0">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {meetingActive ? (
                  <div className="relative">
                    <div className="h-10 w-10 bg-error rounded-full flex items-center justify-center animate-pulse text-white">
                      <Mic className="h-5 w-5" />
                    </div>
                    <span className="absolute -top-1 -right-1 h-3.5 w-3.5 bg-success rounded-full border-2 border-card" />
                  </div>
                ) : (
                  <div className="h-10 w-10 bg-card-muted rounded-full border border-border flex items-center justify-center text-text-muted">
                    <MicOff className="h-5 w-5" />
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-bold text-text-primary">
                    {meetingActive
                      ? "Riunione in Corso"
                      : "Nessuna sessione attiva"}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge
                      variant={meetingActive ? "success" : "neutral"}
                      className="text-[9px] uppercase px-1.5 py-0"
                    >
                      {meetingActive ? "Live Stream" : "Off-line"}
                    </Badge>
                    <span className="text-xs font-mono text-text-muted">
                      Durata: {formatTime(duration)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-2">
                {meetingActive ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setMuted(!muted)}
                      className={`h-9 px-3 gap-1.5 text-xs ${muted ? "border-error text-error hover:bg-error-soft/10" : ""}`}
                    >
                      {muted ? (
                        <MicOff className="h-4 w-4" />
                      ) : (
                        <Mic className="h-4 w-4" />
                      )}
                      {muted ? "Muto" : "Silenzia"}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={endMeeting}
                      className="h-9 px-3 gap-1.5 text-xs"
                    >
                      <PhoneOff className="h-4 w-4" />
                      Chiudi Sessione
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={startMeeting}
                    className="gap-1.5 h-9 text-xs"
                  >
                    <Play className="h-4 w-4" />
                    Avvia Sessione War Room
                  </Button>
                )}
              </div>
            </div>

            {/* Audio waveform */}
            {meetingActive && (
              <div className="mt-5 border border-border/40 rounded-lg bg-card-muted/10 p-2.5">
                <canvas ref={canvasRef} className="w-full h-[60px]" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Live Transcript Log */}
        <Card className="border border-border flex-1 flex flex-col overflow-hidden">
          <CardHeader className="pb-3 border-b border-border/40 shrink-0">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-text-secondary flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-primary-500" />
              Trascrizione Audio Real-Time
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-4 scrollbar-thin space-y-4 bg-card-muted/5 min-h-[300px]">
            {transcript.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-8 text-text-muted">
                <Mic className="h-8 w-8 text-text-muted mb-2 animate-bounce" />
                <p className="text-xs font-semibold text-text-secondary">
                  Console di ascolto vuota
                </p>
                <p className="text-[10px] mt-0.5 text-text-muted">
                  Avvia la sessione audio per visualizzare qui la trascrizione
                  del parlato in tempo reale.
                </p>
              </div>
            ) : (
              transcript.map((line) => (
                <div
                  key={line.id}
                  className={`flex gap-3 text-xs leading-relaxed max-w-[85%] ${
                    line.speaker === "Sistema"
                      ? "mx-auto text-center justify-center max-w-full italic text-text-muted bg-card-muted/50 p-2 rounded-lg border border-border/40 w-full"
                      : line.isAi
                        ? "ml-auto flex-row-reverse text-right bg-primary-500/5 border border-primary-500/10 p-3 rounded-xl"
                        : "mr-auto text-left bg-card p-3 rounded-xl border border-border"
                  }`}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 justify-start font-bold">
                      {line.isAi ? (
                        <div className="flex items-center gap-1 text-primary-600">
                          <Sparkles className="h-3.5 w-3.5 fill-primary-500/20" />
                          <span>{line.speaker}</span>
                        </div>
                      ) : (
                        <span className="text-text-primary">
                          {line.speaker}
                        </span>
                      )}
                      <span className="text-[9px] font-mono text-text-muted font-normal">
                        {line.time}
                      </span>
                    </div>
                    <p className="text-text-secondary leading-relaxed font-sans">
                      {line.text}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={transcriptEndRef} />
          </CardContent>
        </Card>
      </div>

      {/* RIGHT COLUMN: AI Extraction Hub & Graph Sync */}
      <div className="lg:col-span-5 flex flex-col h-[680px] overflow-hidden">
        <Card className="border border-border flex flex-col h-full overflow-hidden">
          <CardHeader className="pb-3 border-b border-border/40 shrink-0">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-text-secondary flex items-center gap-1.5">
                <Sparkles className="h-4 w-4 text-primary-500 fill-primary-500/20" />
                Copilota AI: Insights Rilevati ({detectedInsights.length})
              </CardTitle>
              {meetingActive && !muted && (
                <span className="flex h-2 w-2 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
                </span>
              )}
            </div>
            <CardDescription className="text-xs">
              Formulazioni e nodi estratti in tempo reale. Configura e conferma
              per sincronizzarli nel database.
            </CardDescription>
          </CardHeader>

          {/* List of Insights */}
          <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin min-h-[300px]">
            {errorMessage && (
              <div className="p-3 rounded-lg bg-error-soft/30 border border-error/20 flex items-start gap-2 text-xs text-error">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{errorMessage}</span>
              </div>
            )}

            {detectedInsights.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-8 text-text-muted">
                <Sparkles className="h-8 w-8 text-text-muted mb-2 animate-pulse" />
                <p className="text-xs font-semibold text-text-secondary">
                  Nessun insight rilevato
                </p>
                <p className="text-[10px] mt-0.5 text-text-muted max-w-xs">
                  Ascolterò il parlato e mostrerò qui i task, i rischi e le
                  decisioni non appena pronunciati.
                </p>
              </div>
            ) : (
              detectedInsights.map((insight) => (
                <Card
                  key={insight.id}
                  className={`border transition-all ${
                    insight.synced
                      ? "border-success bg-success-soft/5"
                      : "border-border hover:border-primary-500/50"
                  }`}
                >
                  <CardHeader className="p-4 pb-2 flex flex-row items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      {insight.type === "task" ? (
                        <div className="p-1.5 bg-category-work-soft border border-category-work-border text-category-work rounded-lg">
                          <FileCheck className="h-4 w-4" />
                        </div>
                      ) : insight.type === "risk" ? (
                        <div className="p-1.5 bg-category-risk-soft border border-category-risk-border text-category-risk rounded-lg">
                          <AlertTriangle className="h-4 w-4" />
                        </div>
                      ) : (
                        <div className="p-1.5 bg-category-strategy-soft border border-category-strategy-border text-category-strategy rounded-lg">
                          <TrendingUp className="h-4 w-4" />
                        </div>
                      )}

                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
                          {insight.type === "task"
                            ? "Task Operativo"
                            : insight.type === "risk"
                              ? "Rischio Identificato"
                              : "Decisione Presa"}
                        </span>
                        <CardTitle className="text-xs font-bold text-text-primary leading-tight mt-0.5">
                          {insight.title}
                        </CardTitle>
                      </div>
                    </div>

                    <Badge
                      variant={insight.synced ? "success" : "neutral"}
                      className="text-[9px]"
                    >
                      {insight.synced ? "Sincronizzato" : "Bozza AI"}
                    </Badge>
                  </CardHeader>

                  <CardContent className="p-4 pt-2 pb-3 space-y-3">
                    {/* Input configurations based on type */}
                    {!insight.synced && (
                      <div className="space-y-3 border-t border-border/50 pt-2 text-xs">
                        {insight.type === "task" && (
                          <>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label htmlFor={`project-${insight.id}`}>
                                  Progetto Target
                                </Label>
                                <Select
                                  id={`project-${insight.id}`}
                                  value={insight.projectId || ""}
                                  onChange={(e) =>
                                    updateInsightField(
                                      insight.id,
                                      "projectId",
                                      e.target.value,
                                    )
                                  }
                                  className="h-8 text-xs py-0.5"
                                >
                                  <option value="">
                                    Seleziona progetto...
                                  </option>
                                  {initialProjects.map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {p.title}
                                    </option>
                                  ))}
                                </Select>
                              </div>

                              <div className="space-y-1">
                                <Label htmlFor={`priority-${insight.id}`}>
                                  Priorità
                                </Label>
                                <Select
                                  id={`priority-${insight.id}`}
                                  value={insight.priority || "medium"}
                                  onChange={(e) =>
                                    updateInsightField(
                                      insight.id,
                                      "priority",
                                      e.target.value,
                                    )
                                  }
                                  className="h-8 text-xs py-0.5"
                                >
                                  <option value="low">Bassa</option>
                                  <option value="medium">Media</option>
                                  <option value="high">Alta</option>
                                  <option value="critical">Critica</option>
                                </Select>
                              </div>
                            </div>

                            <div className="space-y-1">
                              <Label htmlFor={`assignee-${insight.id}`}>
                                Assegnato A
                              </Label>
                              <Select
                                id={`assignee-${insight.id}`}
                                value={insight.assigneeId || ""}
                                onChange={(e) =>
                                  updateInsightField(
                                    insight.id,
                                    "assigneeId",
                                    e.target.value,
                                  )
                                }
                                className="h-8 text-xs py-0.5"
                              >
                                <option value="">Nessuno (Backlog)</option>
                                {initialAgents.map((a) => (
                                  <option key={a.id} value={a.id}>
                                    {a.name} ({a.role})
                                  </option>
                                ))}
                              </Select>
                            </div>
                          </>
                        )}

                        {insight.type === "risk" && (
                          <div className="space-y-1">
                            <Label htmlFor={`severity-${insight.id}`}>
                              Severità Rischio
                            </Label>
                            <Select
                              id={`severity-${insight.id}`}
                              value={insight.severity || "medium"}
                              onChange={(e) =>
                                updateInsightField(
                                  insight.id,
                                  "severity",
                                  e.target.value,
                                )
                              }
                              className="h-8 text-xs py-0.5"
                            >
                              <option value="low">Basso</option>
                              <option value="medium">Medio</option>
                              <option value="high">Alto</option>
                              <option value="critical">Critico</option>
                            </Select>
                          </div>
                        )}

                        {insight.type === "decision" && (
                          <>
                            <div className="space-y-1">
                              <Label htmlFor={`context-${insight.id}`}>
                                Contesto Decisione
                              </Label>
                              <textarea
                                id={`context-${insight.id}`}
                                value={insight.context || ""}
                                onChange={(e) =>
                                  updateInsightField(
                                    insight.id,
                                    "context",
                                    e.target.value,
                                  )
                                }
                                rows={2}
                                className="flex w-full rounded-lg border border-border bg-card px-2 py-1 text-xs shadow-sm transition-colors placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500 text-foreground resize-none"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label htmlFor={`decider-${insight.id}`}>
                                Decisore Principale
                              </Label>
                              <Select
                                id={`decider-${insight.id}`}
                                value={insight.assigneeId || ""}
                                onChange={(e) =>
                                  updateInsightField(
                                    insight.id,
                                    "assigneeId",
                                    e.target.value,
                                  )
                                }
                                className="h-8 text-xs py-0.5"
                              >
                                <option value="">Seleziona decisore...</option>
                                {initialAgents.map((a) => (
                                  <option key={a.id} value={a.id}>
                                    {a.name} ({a.role})
                                  </option>
                                ))}
                              </Select>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {insight.synced && (
                      <div className="text-[10px] text-success font-medium bg-success-soft/20 border border-success/30 rounded-lg p-2.5 flex items-center justify-between mt-2">
                        <span className="flex items-center gap-1">
                          <CheckCircle className="h-3.5 w-3.5" />
                          Sincronizzato permanentemente
                        </span>
                        <span className="font-mono text-text-muted">
                          ID: {insight.syncId}
                        </span>
                      </div>
                    )}
                  </CardContent>

                  {!insight.synced && (
                    <CardFooter className="p-4 pt-0 flex justify-end gap-2 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => handleSyncInsight(insight.id)}
                        loading={insight.loading}
                        className="h-8 text-[11px] gap-1 px-3"
                      >
                        <Database className="h-3 w-3" />
                        Sincronizza nel Grafo
                      </Button>
                    </CardFooter>
                  )}
                </Card>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
